import moment from 'moment-timezone';
import { CST_TIMEZONE, formatCstDate } from './cstDate';

export const DASHBOARD_CACHE_TTL_MS = 15 * 60 * 1000;
export const DASHBOARD_MAX_CACHE_ENTRIES = 6;

export const buildDashboardCacheKey = (startDate, endDate) => `${startDate}_${endDate}`;

export const getDaysInRange = (startDate, endDate) => {
  const days = [];
  const start = moment(startDate).tz(CST_TIMEZONE).startOf('day');
  const end = moment(endDate).tz(CST_TIMEZONE).startOf('day');

  let current = start.clone();
  while (current.isSameOrBefore(end, 'day')) {
    days.push(current.clone());
    current.add(1, 'day');
  }

  return days;
};

export const getEplOperationalDateRange = (startDate, endDate) => {
  const start = moment(startDate).tz(CST_TIMEZONE);
  const end = moment(endDate).tz(CST_TIMEZONE);
  const isSingleDay = start.isSame(end, 'day');

  return {
    startDate: formatCstDate(startDate),
    endDate: end.clone().add(1, 'day').format('YYYY-MM-DD'),
    selectedEndDate: formatCstDate(endDate),
    isSingleDay,
  };
};

/** Fast parse for analytics labels like "2026-07-26 09:30 AM" — avoids moment in hot loops */
export const getOperationalDayKeyFast = (timestamp) => {
  if (!timestamp || typeof timestamp !== 'string') return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(timestamp.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  let hour = Number(match[4]);
  const minute = Number(match[5]);
  const meridiem = match[6].toUpperCase();

  if (meridiem === 'PM' && hour !== 12) hour += 12;
  if (meridiem === 'AM' && hour === 12) hour = 0;

  const totalMinutes = hour * 60 + minute;
  const dayStartMinutes = 12 * 60;
  const dayEndMinutes = 11 * 60;

  if (totalMinutes >= dayStartMinutes) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  if (totalMinutes < dayEndMinutes) {
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() - 1);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  return null;
};

const emptySide = () => ({ labels: [], tables: [], game_name: [] });

const createDayBucket = (dayKey, displayDate) => ({
  date: dayKey,
  displayDate,
  eplTchData: {
    EPL: emptySide(),
    Competitor: emptySide(),
  },
  error: null,
});

const pushRecord = (bucket, timestamp, index, source) => {
  bucket.labels.push(timestamp);
  bucket.tables.push(source.tables[index]);
  bucket.game_name.push(source.game_name[index]);
};

/** Single-pass filter + optional day-wise split (much cheaper than separate loops) */
export const processDashboardAnalytics = ({
  rawData,
  startDate,
  endDate,
  isSingleDay,
  rangeStart,
  rangeEnd,
}) => {
  if (!rawData?.EPL || !rawData?.Competitor) {
    return { filteredData: null, dayWiseData: {}, isMultiDay: !isSingleDay };
  }

  const multiDay = !isSingleDay;
  const days = getDaysInRange(rangeStart || startDate, rangeEnd || endDate);
  const validKeys = new Set();
  const dayWiseData = {};

  days.forEach((day) => {
    const dayKey = day.format('YYYY-MM-DD');
    validKeys.add(dayKey);
    if (multiDay) {
      dayWiseData[dayKey] = createDayBucket(dayKey, day.format('MMM DD, YYYY'));
    }
  });

  const filteredData = {
    EPL: emptySide(),
    Competitor: emptySide(),
  };

  const processSide = (sourceKey) => {
    const source = rawData[sourceKey];
    const labels = source.labels || [];

    for (let index = 0; index < labels.length; index += 1) {
      const timestamp = labels[index];
      const dayKey = getOperationalDayKeyFast(timestamp);
      if (!dayKey || !validKeys.has(dayKey)) continue;

      pushRecord(filteredData[sourceKey], timestamp, index, source);

      if (multiDay && dayWiseData[dayKey]) {
        pushRecord(dayWiseData[dayKey].eplTchData[sourceKey], timestamp, index, source);
      }
    }
  };

  processSide('EPL');
  processSide('Competitor');

  return {
    filteredData,
    dayWiseData: multiDay ? dayWiseData : {},
    isMultiDay: multiDay,
  };
};

export const filterEplOperationalData = (data, targetStartDate, targetEndDate, isSingleDay) =>
  processDashboardAnalytics({
    rawData: data,
    startDate: targetStartDate,
    endDate: targetEndDate,
    isSingleDay,
    rangeStart: targetStartDate,
    rangeEnd: targetEndDate,
  }).filteredData;

export const buildDayWiseResultsFromRawData = (rawData, days) => {
  if (!days?.length) return {};
  const rangeStart = days[0].toDate();
  const rangeEnd = days[days.length - 1].toDate();
  return processDashboardAnalytics({
    rawData,
    startDate: days[0].format('YYYY-MM-DD'),
    endDate: days[days.length - 1].format('YYYY-MM-DD'),
    isSingleDay: false,
    rangeStart,
    rangeEnd,
  }).dayWiseData;
};

export const trimDashboardCache = (cache, cacheKeys) => {
  const nextCache = { ...cache };
  const nextKeys = [...cacheKeys];

  while (nextKeys.length > DASHBOARD_MAX_CACHE_ENTRIES) {
    const oldestKey = nextKeys.shift();
    delete nextCache[oldestKey];
  }

  return { cache: nextCache, cacheKeys: nextKeys };
};

export const isDashboardCacheFresh = (entry) =>
  Boolean(entry?.fetchedAt && Date.now() - entry.fetchedAt < DASHBOARD_CACHE_TTL_MS);

/** Yield to the browser so UI stays responsive during heavy work */
export const yieldToBrowser = () =>
  new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });
